import * as React from "react"
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md"
import { Box, HStack, IconButton, SimpleGrid, Stack, useBreakpointValue } from "@chakra-ui/react"
import useEmblaCarousel from "embla-carousel-react"

import { RecommendedConnectionItemFragment } from "lib/graphql"

import { RecommendedConnectionTile } from "./RecommendedConnectionTile"

interface Props {
  connections: RecommendedConnectionItemFragment[]
  connectionsCount: number
}

export function RecommendedConnectionsSlider({ connections, connectionsCount }: Props) {
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const displayCount = isMobile ? 1 : 3

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: displayCount,
    duration: 30,
  })

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setCurrentIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
      setCurrentIndex(0)
    }
  }, [isMobile, emblaApi])

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const canScrollPrev = currentIndex > 0
  const canScrollNext = currentIndex + displayCount < connectionsCount

  if (!isMobile) {
    return (
      <SimpleGrid columns={3} w="100%" gap={8}>
        {connections.map((connection) => (
          <Stack key={`recommended:${connection.id}`} flex={1} w="100%">
            <RecommendedConnectionTile connection={connection} />
          </Stack>
        ))}
      </SimpleGrid>
    )
  }

  return (
    <Box id="my-embla-carousel-recommended-connections" position="relative">
      {connectionsCount > displayCount && (
        <>
          <IconButton
            zIndex={500}
            position="absolute"
            top="85px"
            left={0}
            colorScheme="gray"
            variant="ghost"
            aria-label="vorige dia"
            icon={<MdKeyboardArrowLeft size={48} />}
            onClick={(e) => {
              e.stopPropagation()
              scrollPrev()
            }}
            disabled={!canScrollPrev}
            width="3rem"
          />
          <IconButton
            zIndex={500}
            position="absolute"
            top="85px"
            right={0}
            colorScheme="gray"
            variant="ghost"
            aria-label="volgende dia"
            icon={<MdKeyboardArrowRight size={48} />}
            onClick={(e) => {
              e.stopPropagation()
              scrollNext()
            }}
            disabled={!canScrollNext}
            width="3rem"
            ml={5}
          />
        </>
      )}
      <Box className="embla" ref={emblaRef} overflow="hidden">
        <Box className="embla__container" display="flex">
          {connections.map((connection) => (
            <HStack
              key={`recommended:${connection.id}`}
              className="embla__slide"
              flex={`0 0 calc(${100 / displayCount}% - ${displayCount > 1 ? 15 : 0}px)`}
              minW={0}
              mr={displayCount > 1 ? "30px" : 0}
              m={0}
              p={0}
              spacing={0}
            >
              <RecommendedConnectionTile connection={connection} />
            </HStack>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
